import { fileTypeLabel } from "@/lib/utils";

/*
 * How the Data Room decides what counts as "one document" with a version
 * history, versus two separate documents.
 *
 * The old rule was: one row per `file_type`, newest wins, everything else
 * hidden behind an "N versions" label. That reads as data loss. A client
 * bulk-uploaded three files, typed them all "Pitch Deck", and reported the bug
 * as "Bulk Upload only loaded 1 of 3 files" — all three had uploaded, two were
 * simply invisible.
 *
 * The rule here is that a version is a *re-upload of the same document*, which
 * takes three things to identify:
 *
 *   kind      — a real file and the AI's extracted/anonymized text derived from
 *               it are different artifacts with different delete paths, so they
 *               never stack. They would otherwise collide often: a file with no
 *               stored name falls back to `${typeLabel}.${ext}` ("Pitch
 *               Deck.pdf"), which normalises to the same string as the
 *               extracted row's name ("Pitch Deck").
 *   file_type — the document slot it occupies.
 *   name      — normalised, because "Deck.PDF" and "deck.pdf " are the same
 *               document to a human.
 *
 * Known and accepted: DocSend imports are deliberately renamed after their type
 * label (docsend-picker.tsx) because DocSend's own filename is a random hash,
 * and Drive paste-links do the same. Two DocSend decks both typed "Pitch Deck"
 * therefore still stack. Undoing that renaming would put hash filenames back in
 * front of users, which was itself a deliberate fix. The collapse is no longer
 * silent — `queueCollisions` warns before the upload and `describeBulkOutcome`
 * reports it after — and the reported bug was local upload, where names are
 * real.
 */

/** The fields grouping needs. Deliberately structural: callers pass their own
 *  richer row objects and get the same objects back. */
export type GroupableFile = {
  id: string;
  name: string;
  fileType?: string;
  /** "File" for an uploaded document; "Extracted"/"Anonymized" for AI output. */
  operation: string;
  /** Formatted date, or "-" when the row has no real timestamp. */
  addedOn: string;
  timestamp: string;
};

export type FileGroup<T extends GroupableFile> = {
  key: string;
  /** The document type shared by every version in this group. */
  fileType: string;
  /** Newest version — the row the Data Room renders. */
  latest: T;
  /** Every version, newest first. Length 1 when there is no history. */
  versions: T[];
};

/** Extensions we strip before comparing names, so "Deck.pdf" and "Deck" match. */
const STRIPPABLE_EXTENSION =
  /\.(pdf|docx?|xlsx?|pptx?|csv|txt|png|jpe?g|gif|webp|ico|json|md)$/i;

/**
 * Two names refer to the same document when they differ only by case, padding,
 * internal whitespace runs, or a file extension.
 */
export function normalizeGroupName(name: string): string {
  return name
    .trim()
    .replace(STRIPPABLE_EXTENSION, "")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/**
 * ` ` as the separator: it cannot occur in a file type or a filename, so
 * no pair of distinct inputs can be made to produce the same key.
 */
export function groupKeyFor(file: GroupableFile): string {
  // Preserved from the original grouping: rows with no `fileType` fall back to
  // the leading segment of the name rather than bucketing together under "".
  const fileType = file.fileType || file.name.split(".")[0].toLowerCase();
  const kind = file.operation === "File" ? "file" : `derived:${file.operation}`;
  return `${kind} ${fileType} ${normalizeGroupName(file.name)}`;
}

/**
 * Newest first. Rows with no real date ("-") sort last rather than being
 * treated as epoch-zero, and an unparseable timestamp never throws.
 *
 * Copies before sorting. The original did not, and `handleShowHistory` re-sorted
 * the very arrays the render pass was holding.
 */
export function sortByRecency<T extends GroupableFile>(files: T[]): T[] {
  return [...files].sort((a, b) => {
    if (a.addedOn === "-" && b.addedOn === "-") return 0;
    if (a.addedOn === "-") return 1;
    if (b.addedOn === "-") return -1;

    try {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);

      if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;

      return dateB.getTime() - dateA.getTime();
    } catch {
      return 0;
    }
  });
}

/**
 * One group per document, in the order each first appears, so the Data Room's
 * row order does not shuffle when a version is added.
 */
export function groupFiles<T extends GroupableFile>(files: T[]): FileGroup<T>[] {
  const buckets = new Map<string, T[]>();

  for (const file of files) {
    const key = groupKeyFor(file);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(file);
    else buckets.set(key, [file]);
  }

  return Array.from(buckets, ([key, bucket]) => {
    const versions = sortByRecency(bucket);
    return {
      key,
      fileType:
        versions[0].fileType ||
        versions[0].name.split(".")[0].toLowerCase(),
      latest: versions[0],
      versions,
    };
  });
}

/** A row in the bulk-upload queue, before anything has been sent. */
export type QueuedUpload = {
  id: string;
  fileName: string;
  fileType: string;
};

export type QueueCollisions = {
  /** Rows that will genuinely stack into one document. */
  collapses: { label: string; fileName: string; count: number }[];
  /** Rows sharing a type but staying separate — worth saying for a different
   *  reason, see `describeSharedType`. */
  sharedTypes: { label: string; count: number }[];
};

/**
 * What to warn about before a bulk upload runs.
 *
 * Two distinct cases, because after the grouping fix they have different
 * consequences and one warning covering both would be false:
 *
 *   collapses    — same type *and* name: these really do become versions of one
 *                  document, and only the newest will be on screen.
 *   sharedTypes  — same type, different names: separate rows. Still worth
 *                  mentioning, because the AI ingestion layer keys on
 *                  (company, file_type) and treats a second file of a type as an
 *                  update to that slot.
 */
export function queueCollisions(queue: QueuedUpload[]): QueueCollisions {
  const byNameAndType = new Map<string, QueuedUpload[]>();
  const byType = new Map<string, QueuedUpload[]>();

  for (const row of queue) {
    if (!row.fileType) continue;

    const nameKey = `${row.fileType} ${normalizeGroupName(row.fileName)}`;
    const nameBucket = byNameAndType.get(nameKey);
    if (nameBucket) nameBucket.push(row);
    else byNameAndType.set(nameKey, [row]);

    const typeBucket = byType.get(row.fileType);
    if (typeBucket) typeBucket.push(row);
    else byType.set(row.fileType, [row]);
  }

  const collapses = Array.from(byNameAndType.values())
    .filter((rows) => rows.length > 1)
    .map((rows) => ({
      label: fileTypeLabel(rows[0].fileType),
      fileName: rows[0].fileName,
      count: rows.length,
    }));

  // A type is only reported as "shared" when the rows are actually distinct
  // documents — otherwise the collapse warning above already covers them, and
  // saying both would double-report the same two files.
  const sharedTypes = Array.from(byType.entries())
    .map(([fileType, rows]) => {
      const distinct = new Set(rows.map((r) => normalizeGroupName(r.fileName)));
      return { label: fileTypeLabel(fileType), count: distinct.size };
    })
    .filter((entry) => entry.count > 1);

  return { collapses, sharedTypes };
}

export type BulkOutcome = {
  filesUploaded: number;
  newDocuments: number;
  newVersions: number;
  /** Type labels that gained a version, for the outcome sentence. */
  versionLabels: string[];
};

/**
 * What actually landed, measured against the groups that existed before the
 * batch started. Called with the pre-upload key set so a file that stacked onto
 * an existing document is counted as a version, not a new row.
 */
export function summarizeBulkOutcome({
  uploaded,
  existingKeys,
}: {
  uploaded: QueuedUpload[];
  existingKeys: Set<string>;
}): BulkOutcome {
  const seen = new Set(existingKeys);
  let newDocuments = 0;
  let newVersions = 0;
  const versionLabels: string[] = [];

  for (const row of uploaded) {
    const key = groupKeyFor({
      id: row.id,
      name: row.fileName,
      fileType: row.fileType,
      operation: "File",
      addedOn: "",
      timestamp: "",
    });

    if (seen.has(key)) {
      newVersions++;
      const label = fileTypeLabel(row.fileType);
      if (!versionLabels.includes(label)) versionLabels.push(label);
    } else {
      seen.add(key);
      newDocuments++;
    }
  }

  return {
    filesUploaded: uploaded.length,
    newDocuments,
    newVersions,
    versionLabels,
  };
}

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

/**
 * The post-upload sentence. Always states the file count, and only mentions
 * versions when something actually stacked — so a collapse is never silent, and
 * the ordinary case stays short.
 */
export function describeBulkOutcome(outcome: BulkOutcome): string {
  const { filesUploaded, newDocuments, newVersions, versionLabels } = outcome;
  const uploaded = `Uploaded ${plural(filesUploaded, "file")}`;

  if (newVersions === 0) return uploaded;

  const versions =
    versionLabels.length > 0
      ? `${plural(newVersions, "new version")} of ${versionLabels.join(", ")}`
      : plural(newVersions, "new version");

  if (newDocuments === 0) return `${uploaded} — ${versions}`;
  return `${uploaded} — ${plural(newDocuments, "new document")}, ${versions}`;
}

/** Copy for the neutral same-type notice. See `queueCollisions`. */
export function describeSharedType(entry: {
  label: string;
  count: number;
}): string {
  return `${entry.count} files are set to ${entry.label}. They'll appear as separate documents; the deal's ${entry.label} analysis follows the most recently processed one.`;
}

/** Copy for the amber collapse warning. See `queueCollisions`. */
export function describeCollapse(entry: {
  label: string;
  fileName: string;
  count: number;
}): string {
  return `${entry.count} files named "${entry.fileName}" are set to ${entry.label} — they'll be shown as versions of one document.`;
}
